// @ts-nocheck
import * as React from "react";
import { Logo, Ic, Sidebar, Topbar, GuardianTabBar, LineChart, BarChart, Kpi, AvatarImg, useCurrentUser, apiErrorMessage, supportPhone } from "./common";

const RISK_LABEL = { critical: "위험", warning: "주의", stable: "편안" };
const RISK_BG = { critical: "rgba(220,139,130,0.20)", warning: "rgba(232,188,87,0.20)", stable: "rgba(143,200,168,0.20)" };
const RISK_FG = { critical: "#DC8B82", warning: "#E8BC57", stable: "#9BD0AA" };
const STATUS_LABEL = { planned: "예정", in_progress: "진행중", completed: "완료", cancelled: "취소" };
// 케어 카드 단계별 색. assigned/caring=진행(초록), 그 외 진행중 단계=대기(노랑),
// closed=종결(회색). 백엔드 care_status.stage(CaseRecord status + "review")와 대응.
const CARE_STAGE_FG = {
  review: "#E8BC57", applying: "#E8BC57", matching: "#E8BC57",
  assigned: "#9BD0AA", caring: "#9BD0AA", closed: "#A89A7C",
};
const MGR_STATUS_BADGE = {
  available:  { c: "green",  l: "가용" },
  busy:       { c: "amber",  l: "다른 방문 중" },
  overloaded: { c: "rose",   l: "과부하" },
  moving:     { c: "orange", l: "이동 중" },
  offline:    { c: "gray",   l: "오프라인" },
};

function fmtClock(d) {
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

// 계정에 연결된 어르신이 없는 보호자용 빈 상태. 신규 모델에선 가입 시 어르신이
// 반드시 연결되므로(=elderly_id 항상 채워짐) 이 상태는 사실상 레거시 계정 전용이다.
// 케어 신청은 회원가입으로만 접수하고 로그인 후 어르신을 추가 연결하는 동선은 아직
// 없으므로, 막다른 폼 대신 고객센터/권역 문의로 안내한다.
function GuardianEmptyApply({ name }) {
  return (
    <div className="app" style={{background:'var(--bg)', overflow:'hidden', display:'flex', flexDirection:'column'}}>
      <div style={{padding:'18px 22px 6px'}}>
        <div style={{fontSize:11, color:'var(--ink-3)'}}>안녕하세요, {name || '보호자'}님</div>
        <div style={{fontSize:18, fontWeight:800}}>연결된 어르신이 없어요</div>
      </div>

      <div style={{flex:1, display:'flex', flexDirection:'column', justifyContent:'center', padding:'0 22px'}}>
        <div className="card" style={{padding:22, borderRadius:18, textAlign:'center'}}>
          <div style={{width:56, height:56, borderRadius:16, margin:'0 auto 14px', background:'var(--orange-50)', color:'var(--orange-600)', display:'grid', placeItems:'center'}}>
            <span style={{width:28, height:28, display:'grid', placeItems:'center', fontSize:28}}>{Ic.user}</span>
          </div>
          <div style={{fontSize:16, fontWeight:800, marginBottom:6}}>계정에 연결된 어르신이 없어요</div>
          <div style={{fontSize:12.5, color:'var(--ink-3)', lineHeight:1.6, marginBottom:18}}>
            케어 신청은 회원가입 시 어르신 정보를 등록하면 함께 접수됩니다.
            이미 가입된 계정에 어르신을 연결하려면 권역 관리자 또는 고객센터로
            문의해 주세요.
          </div>
          <a href={`tel:${supportPhone()}`} className="btn btn-primary btn-lg" style={{width:'100%', textDecoration:'none', display:'flex', justifyContent:'center'}}>고객센터 문의 {supportPhone()}</a>
          <a href="/m/guardian-manual" style={{display:'inline-block', marginTop:12, fontSize:11, color:'var(--ink-3)', textDecoration:'none'}}>처음이신가요? 사용자 매뉴얼 보기 →</a>
        </div>
      </div>

      <div style={{marginTop:'auto'}}>
        <GuardianTabBar active="home"/>
      </div>
      <div style={{height:4, width:120, background:'var(--ink-1)', borderRadius:99, margin:'2px auto 6px'}}/>
    </div>
  );
}

function DashboardGuardian() {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  // 어르신 미연결 보호자(아직 케어 신청 전)는 백엔드가 403을 준다 — 이 화면 페이지
  // 가드가 이미 guardian/citizen만 통과시키므로, 여기서의 403은 "연결된 어르신 없음"을
  // 뜻한다. 빨간 에러 대신 케어 신청으로 가는 빈 상태 CTA로 분기한다.
  const [needsApply, setNeedsApply] = React.useState(false);
  const [now, setNow] = React.useState(() => new Date());
  const { user } = useCurrentUser();

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/v1/dashboard/guardian", { credentials: "same-origin" });
        const body = await r.json().catch(() => null);
        if (cancelled) return;
        if (!r.ok) {
          // 401/403는 분기해서 사용자가 이해할 안내로. 그 외는 백엔드 메시지/상태 fallback.
          // 에러 본문을 setData로 흘리지 않도록 분리 — 잘못된 렌더 방지.
          if (r.status === 401)      setErr("로그인이 필요합니다. /login 으로 이동해 다시 시도하세요.");
          else if (r.status === 403) setNeedsApply(true); // 연결된 어르신 없음 → 신청 유도
          else                       setErr(apiErrorMessage(body, r.status));
          return;
        }
        setErr(null);
        setData(body);
      } catch (e) {
        if (!cancelled) setErr(apiErrorMessage(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live status-bar clock. Tick every 30s — minute-level resolution is
  // plenty for the "9:41"-style mobile status bar at the top of the screen.
  React.useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  if (needsApply) return <GuardianEmptyApply name={user?.name} />;
  if (err)   return <div className="app" style={{padding:40, color:'var(--rose-500)'}}>API 오류: {err}</div>;
  if (!data) return <div className="app muted" style={{padding:40}}>불러오는 중…</div>;

  const e = data.elderly;
  const mgr = data.assigned_manager;
  // 케어 진행 상태 — 구버전 백엔드(care_status 미포함) 대비 안전 폴백.
  const care = data.care_status || { stage: "review", label: "진행 중", active: false };

  return (
    <div className="app" style={{background:'var(--bg)', overflow:'hidden'}}>
      <div style={{display:'flex', justifyContent:'space-between', padding:'8px 22px 4px', fontSize:11, fontWeight:600, color:'var(--ink-1)'}}>
        <span>{fmtClock(now)}</span>
        <div style={{display:'flex', gap:5, alignItems:'center'}}>
          <span>●●●</span><span style={{fontSize:10}}>5G</span>
          <span style={{width:18, height:9, border:'1px solid var(--ink-1)', borderRadius:2, position:'relative'}}><span style={{position:'absolute', inset:1, background:'var(--ink-1)'}}/></span>
        </div>
      </div>

      <div style={{padding:'14px 22px 6px', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
        <div>
          <div style={{fontSize:11, color:'var(--ink-3)'}}>안녕하세요, {user?.name || '보호자'}님</div>
          <div style={{fontSize:18, fontWeight:800}}>오늘 어머니는 <span style={{color: e.risk_level==='stable'?'var(--green-600)':'var(--orange-600)'}}>{RISK_LABEL[e.risk_level]}</span>해요</div>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:14}}>
          <a href="/m/guardian-manual" title="보호자 사용자 매뉴얼"
            style={{display:'inline-flex', alignItems:'center', gap:4, padding:'5px 9px', borderRadius:8, border:'1px solid var(--border-soft)', background:'var(--surface)', color:'var(--ink-1)', fontSize:11, fontWeight:700, textDecoration:'none', whiteSpace:'nowrap'}}>
            <span style={{display:'inline-flex', width:13, height:13, color:'var(--orange-600)'}}>{Ic.help}</span>
            사용자 매뉴얼
          </a>
          <a href="/m/notify" style={{position:'relative', color:'var(--ink-1)', textDecoration:'none'}}
             aria-label={`알림 ${data.unread_notifications || 0}건`}>
            <span style={{width:22, height:22, display:'grid', placeItems:'center', fontSize:22}}>{Ic.bell}</span>
            {(data.unread_notifications || 0) > 0 && (
              <span style={{position:'absolute', top:-2, right:-2, minWidth:14, height:14, padding:'0 3px', borderRadius:99, background:'var(--orange-500)', color:'#fff', fontSize:9, fontWeight:800, display:'grid', placeItems:'center'}}>
                {data.unread_notifications > 99 ? '99+' : data.unread_notifications}
              </span>
            )}
          </a>
        </div>
      </div>

      <div style={{margin:'14px 22px 0', padding:18, background:'linear-gradient(135deg, #2A241F, #3E3329)', color:'#F5EBD8', borderRadius:18, position:'relative', overflow:'hidden'}}>
        <div style={{position:'absolute', top:-40, right:-30, width:140, height:140, borderRadius:'50%', background:'rgba(232,130,91,0.15)'}}/>
        <div style={{display:'flex', alignItems:'center', gap:12}}>
          <AvatarImg className="avatar lg" style={{background:'linear-gradient(135deg, #EFB287, #E8825B)'}} url={e.avatar_url} name={e.name}/>
          <div>
            <div style={{fontSize:11, opacity:.7}}>어머니 · {e.age}세</div>
            <div style={{fontSize:16, fontWeight:800, color:'#fff'}}>{e.name}</div>
            <div style={{fontSize:10, color:'#A89A7C', marginTop:2}}>{e.health_status}</div>
          </div>
          <span style={{marginLeft:'auto', padding:'4px 10px', background: RISK_BG[e.risk_level], color: RISK_FG[e.risk_level], borderRadius:999, fontSize:10, fontWeight:700}}>{RISK_LABEL[e.risk_level]}</span>
        </div>
        <div style={{display:'flex', gap:8, marginTop:14}}>
          {[
            {l:'복약', v:`${e.medications.length}종`, s:e.medications.slice(0,2).join(', ')||'없음'},
            {l:'케어', v:care.label, s:e.address, c:CARE_STAGE_FG[care.stage]||'#fff'},
            {l:'위험', v:RISK_LABEL[e.risk_level], s:e.risk_level==='stable'?'안정':'관찰 필요'},
          ].map((s,i)=>(
            <div key={i} style={{flex:1, padding:10, background:'rgba(255,255,255,0.06)', borderRadius:10, overflow:'hidden'}}>
              <div style={{fontSize:10, opacity:.65}}>{s.l}</div>
              <div style={{fontSize:14, fontWeight:700, color:s.c||'#fff', marginTop:2}}>{s.v}</div>
              <div style={{fontSize:9, color:'#A89A7C', marginTop:1, textOverflow:'ellipsis', overflow:'hidden', whiteSpace:'nowrap'}}>{s.s}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 응급SOS 빠른 진입 — 긴급 상황 시 즉시 퀵매칭. 보호자 홈 상단 고정. */}
      <div style={{padding:'14px 22px 0'}}>
        <a href="/m/sos" aria-label="응급SOS 신청"
           style={{display:'flex', alignItems:'center', gap:12, padding:'14px 16px', borderRadius:16, textDecoration:'none',
                   background:'linear-gradient(135deg, var(--rose-500), var(--rose-600))', color:'#fff', boxShadow:'0 12px 26px -10px rgba(201,89,79,0.42)'}}>
          <span style={{width:40, height:40, borderRadius:12, background:'rgba(255,255,255,0.18)', display:'grid', placeItems:'center', flex:'0 0 auto'}}>
            <span style={{width:20, height:20, display:'grid', placeItems:'center', fontSize:20}}>{Ic.alert}</span>
          </span>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:14, fontWeight:800}}>응급SOS 신청</div>
            <div style={{fontSize:11, color:'var(--rose-50)', marginTop:1, opacity:.92}}>긴급 상황 시 가까운 응급기관 자동 배정 · 담당자·케어매니저 즉시 대응</div>
          </div>
          <span aria-hidden style={{fontSize:20, opacity:.85}}>›</span>
        </a>
      </div>

      {mgr && (
      <div style={{padding:'18px 22px 0'}}>
        <div style={{fontSize:13, fontWeight:700, marginBottom:10, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
          <span>오늘 방문 매니저</span>
          <a href="/m/timeline" style={{fontSize:11, color:'var(--orange-600)', fontWeight:600, textDecoration:'none'}}>전체 →</a>
        </div>
        <div className="card" style={{padding:14, borderRadius:14, display:'flex', alignItems:'center', gap:12}}>
          {/* 카드 본문을 매니저 상세로 가는 링크로 감싼다. 전화 버튼은 anchor 중첩
              방지를 위해 sibling으로 분리 — 카드 표면 대부분이 클릭 가능하고,
              전화는 별도 동선으로 유지. */}
          <a href="/m/guardian-manager" aria-label={`${mgr.name} 매니저 상세 보기`}
             style={{flex:1, minWidth:0, display:'flex', alignItems:'center', gap:12, textDecoration:'none', color:'inherit'}}>
            <AvatarImg className="avatar lg green" url={mgr.avatar_url} name={mgr.name}/>
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontSize:13, fontWeight:700}}>{mgr.name} 매니저</div>
              <div style={{fontSize:10, color:'var(--ink-3)'}}>{mgr.qualification}</div>
              <div style={{display:'flex', gap:6, marginTop:6, flexWrap:'wrap'}}>
                {data.upcoming_visit ? (
                  <span className="badge badge-orange">
                    {fmtClock(new Date(data.upcoming_visit.scheduled_at))} {data.upcoming_visit.status === 'in_progress' ? '방문 중' : '도착 예정'}
                  </span>
                ) : (
                  <span className="badge badge-gray">오늘 예정 방문 없음</span>
                )}
                {mgr.status && MGR_STATUS_BADGE[mgr.status] && (
                  <span className={"badge badge-" + MGR_STATUS_BADGE[mgr.status].c}>{MGR_STATUS_BADGE[mgr.status].l}</span>
                )}
              </div>
            </div>
            <span aria-hidden style={{color:'var(--ink-3)', fontSize:18, marginLeft:2}}>›</span>
          </a>
          {mgr.phone ? (
            <a href={`tel:${mgr.phone}`} aria-label={`${mgr.name} 매니저에게 전화`}
              style={{width:34, height:34, borderRadius:99, background:'var(--green-500)', color:'#fff', display:'grid', placeItems:'center', textDecoration:'none'}}>
              <span style={{width:18, height:18, display:'grid', placeItems:'center', fontSize:18}}>{Ic.phone}</span>
            </a>
          ) : (
            <button type="button" disabled
              title="전화번호가 등록되어 있지 않습니다"
              style={{width:34, height:34, borderRadius:99, background:'var(--ink-4)', color:'#fff', border:'none', display:'grid', placeItems:'center', cursor:'not-allowed', opacity:0.6}}>
              <span style={{width:18, height:18, display:'grid', placeItems:'center', fontSize:18}}>{Ic.phone}</span>
            </button>
          )}
        </div>
      </div>
      )}

      <div style={{padding:'18px 22px 0'}}>
        <div style={{fontSize:13, fontWeight:700, marginBottom:10}}>최근 방문 ({data.recent_visits.length}건)</div>
        <div className="card" style={{padding:14, borderRadius:14}}>
          {data.recent_visits.length === 0 && <div className="muted" style={{fontSize:12}}>최근 방문 기록 없음</div>}
          {data.recent_visits.map((v, i) => {
            const dt = new Date(v.scheduled_at);
            return (
              <div key={v.id} style={{display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderTop: i>0?'1px solid var(--border-soft)':'none'}}>
                <div style={{textAlign:'center', minWidth:34}}>
                  <div style={{fontSize:9, fontWeight:700, color:'var(--ink-3)'}}>{['일','월','화','수','목','금','토'][dt.getDay()]}</div>
                  <div style={{fontSize:18, fontWeight:800, color:'var(--ink-1)', lineHeight:1}}>{dt.getDate()}</div>
                </div>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:12.5, fontWeight:600}}>{dt.toLocaleTimeString('ko-KR',{hour:'2-digit',minute:'2-digit'})} · {v.manager_name}</div>
                </div>
                <span className={"badge " + (v.status==='completed'?'badge-green':v.status==='in_progress'?'badge-orange':'badge-gray')}>{STATUS_LABEL[v.status]}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{marginTop:'auto'}}>
        <GuardianTabBar active="home"/>
      </div>
      <div style={{height:4, width:120, background:'var(--ink-1)', borderRadius:99, margin:'2px auto 6px'}}/>
    </div>
  );
}
export { DashboardGuardian };
